/**
 * PublicPreviewBanner — Contest / public-demo signal.
 *
 * Non-dismissible-per-load informational strip that appears at the top
 * of every AI-generation-adjacent page. Communicates that AI content
 * generation is intentionally disabled for the public preview while
 * the Business Brain, workflows, and architecture remain fully
 * interactive.
 *
 * Visibility rules (all must be true):
 *   1. `REACT_APP_PUBLIC_PREVIEW` is truthy at build time.
 *   2. The current pathname matches an AI-generation surface.
 *   3. The user has not dismissed the banner during THIS browser
 *      session (session-scoped opt-out).
 *
 * Non-blocking — the banner never gates the UI. It is informational.
 */
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchMe } from "@/entities/workspace/api";

const STORAGE_KEY = "mos.public_preview_banner_dismissed";

// Path prefixes considered "AI-generation surfaces". Kept explicit so
// non-generation pages (billing, workspace settings, etc.) never see it.
const AI_GENERATION_PREFIXES = [
  "/ai-studio",
  "/studio",
  "/campaigns/new",
  "/campaigns/",         // catches /campaigns/:id/room, /campaigns/:id/strategy
  "/campaign-commander",
  "/marketing-strategy",
  "/campaign-room",
  "/media",
  "/history",
  "/generation-history",
  "/publishing",         // publishing composer surfaces generation
];

function pathMatchesGenerationSurface(pathname: string): boolean {
  return AI_GENERATION_PREFIXES.some((prefix) =>
    pathname === prefix || pathname.startsWith(prefix + "/") || pathname.startsWith(prefix),
  );
}

function isPreviewMode(): boolean {
  // Vite exposes REACT_APP_* vars via import.meta.env (see vite.config.ts).
  const raw = (import.meta.env.REACT_APP_PUBLIC_PREVIEW ?? "").toString().toLowerCase();
  return raw === "true" || raw === "1" || raw === "yes";
}

export function PublicPreviewBanner(): JSX.Element | null {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Preview flag is build-time — if it flips off in a later deploy, the
  // banner disappears automatically (component returns null).
  const previewOn = isPreviewMode();

  // Admins (``is_dev_user``) bypass the banner entirely — they have full
  // AI generation access. Query is cheap and shared with ``useMe()``.
  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    enabled: previewOn,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const isAdmin = meQuery.data?.is_dev_user === true;

  useEffect(() => {
    // Reset dismissal if the flag flips off between renders (e.g. env
    // change after enable). Cheap safety net.
    if (!previewOn && dismissed) {
      setDismissed(false);
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* noop */
      }
    }
  }, [previewOn, dismissed]);

  if (!previewOn) return null;
  if (isAdmin) return null;
  if (!pathMatchesGenerationSurface(pathname)) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* noop */
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="public-preview-banner"
      className="border-b border-sky-500/30 bg-sky-500/10 text-sky-100"
    >
      <div className="mx-auto flex max-w-[1400px] items-start gap-4 px-4 py-3 md:px-6 md:py-4">
        <div className="mt-[2px] hidden select-none text-xl md:block" aria-hidden>
          🚀
        </div>
        <div className="min-w-0 flex-1 space-y-2 text-sm leading-relaxed">
          <p className="text-sky-50">
            <span className="mr-2 inline-block rounded-full border border-sky-400/40 bg-sky-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-sky-100">
              Public Preview
            </span>
            You&apos;re exploring an early preview of{" "}
            <span className="font-semibold text-white">Orkexra</span>, the
            first department of the future{" "}
            <span className="font-semibold text-white">
              Orkexra Business Operating System
            </span>
            .
          </p>
          <p className="text-sky-100/90">
            The <span className="font-medium text-sky-50">Business Brain</span>,
            planning engine, workflows, and campaign architecture are fully
            interactive so you can experience how the platform thinks and
            organizes work.
          </p>
          <p className="text-sky-100/90">
            <span className="font-medium text-sky-50">AI content generation</span>{" "}
            has been intentionally disabled for this public preview to conserve
            AI resources during the contest and prevent abuse.
          </p>
          <p className="text-sky-100/90">
            When enabled,{" "}
            <span className="font-medium text-sky-50">Akinex</span> will generate
            complete campaigns — including strategy, social content, blogs,
            emails, images, videos, landing pages, and more — using the
            knowledge stored in the Business Brain.
          </p>
          <p className="text-sky-100/80">
            Feel free to explore the interface, Business Brain, onboarding,
            campaign planner, and workflow architecture. Thank you for taking
            the time to explore Orkexra.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss public preview banner for this session"
          data-testid="public-preview-banner-dismiss"
          className="ml-2 rounded-md p-1 text-sky-200 transition hover:bg-sky-400/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-400/60"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

export default PublicPreviewBanner;
